import { useState, useEffect } from 'react';
import api from '../utils/api';
import Badge from './Badge';
import EntityLink from './EntityLink';
import { useToast } from '../context/FeedbackContext';

export default function OutgoingWOPanel({ woId, onClose }) {
  const toast = useToast();
  const [data, setData] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [apInvoices, setApInvoices] = useState([]);

  useEffect(() => {
    api.get('/work-orders/' + woId).then(r => setData(r.data)).catch(() => setData(null));
    api.get('/ap-invoices?wo_id=' + woId).then(r => setApInvoices(r.data || [])).catch(() => setApInvoices([]));
  }, [woId]);

  async function handleDownload() {
    if (!data) return;
    setDownloading(true);
    try {
      const resp = await api.get('/work-orders/' + woId + '/vendor-copy', { responseType: 'blob' });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'vendor-copy-' + (data.outgoing_wo_number || data.client_wo_number) + '.pdf';
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast('Failed to generate Vendor Copy PDF', 'error');
    } finally {
      setDownloading(false);
    }
  }

  return (
    <>
      <div className="panel-overlay" onClick={onClose} />
      <div className="quick-panel" style={{ width: 480 }}>
        <div className="drawer-header">
          <h3 className="drawer-title">{data?.outgoing_wo_number || 'Loading…'}</h3>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {data && <Badge variant={data.status === 'active' ? 'success' : 'neutral'}>{data.status}</Badge>}
            {data && data.vendor_id && (
              <button
                className="btn btn-primary btn-sm"
                onClick={handleDownload}
                disabled={downloading}
              >
                {downloading ? 'Generating…' : '⬇ Download Vendor Copy'}
              </button>
            )}
            <button className="drawer-close" onClick={onClose}>×</button>
          </div>
        </div>

        {!data && (
          <div className="panel-body">
            <div style={{ textAlign: 'center', padding: 20, color: 'var(--muted)' }}>Loading…</div>
          </div>
        )}

        {data && (
          <div className="panel-body">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, fontSize: 13 }}>
              <DetailRow label="Outgoing WO #" value={data.outgoing_wo_number || '—'} />
              <DetailRow label="Status" value={<Badge variant={data.status === 'active' ? 'success' : 'neutral'}>{data.status}</Badge>} />
              <DetailRow label="Vendor" value={
                data.vendor_id
                  ? <EntityLink type="vendor" id={data.vendor_id}>{data.vendor_name}</EntityLink>
                  : '—'
              } />
              <DetailRow label="Contractor" value={
                data.contractor_id
                  ? <EntityLink type="contractor" id={data.contractor_id}>{data.contractor_name}</EntityLink>
                  : '—'
              } />
              <DetailRow label="Buy Rate" value={data.buy_rate ? `$${data.buy_rate}/hr` : '—'} />
              <DetailRow label="Period" value={data.start_date && data.end_date ? `${data.start_date} – ${data.end_date}` : '—'} />
              <DetailRow label="Client WO #" value={data.client_wo_number || '—'} />
            </div>

            {/* AP Invoices */}
            <div style={{ marginTop: 20 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>
                AP Invoices ({apInvoices.length})
              </div>
              {(!data.ap_invoices || data.ap_invoices?.length === 0) && (
                <div style={{ color: 'var(--muted)', fontSize: 12 }}>No AP invoices linked.</div>
              )}
              {apInvoices.map(inv => (
                <div key={inv.id} style={{ background: 'var(--surface)', borderRadius: 'var(--radius)', padding: '8px 12px', marginBottom: 8, fontSize: 12 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <EntityLink type="ap_invoice" id={inv.id} style={{ fontWeight: 700, fontSize: 13 }}>{inv.invoice_number}</EntityLink>
                    <Badge>{inv.status}</Badge>
                  </div>
                  <div style={{ color: 'var(--muted)', marginTop: 4 }}>
                    {inv.invoice_date} · ${(inv.amount_to_pay || inv.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </>
  );
}

function DetailRow({ label, value }) {
  if (value === null || value === undefined || value === '') return null;
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <span style={{ color: 'var(--muted)' }}>{label}</span>
      <span style={{ fontWeight: 600 }}>{value}</span>
    </div>
  );
}
